import { Row, Col, Container, Button } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import {
  ListTable,
  LoadingSpinner,
  ModalMessage,
  PaginationMenu,
  SortDropdown,
} from "../../components";
import { useState, useEffect, useCallback } from "react";
import {
  adminFetchProductsApi,
  adminDeleteProductApi,
} from "../../api/productsApi.js";
import { useSelector } from "react-redux";
import { getStorageToken, useLogout } from "../../utilities";
import { deleteCloudinaryImagesApi } from "../../api/cloudinaryApi";

export default function AdminProductsList() {
  // displays list of products
  // user can add/edit/delete products
  // user can sort/filter products list

  const tableHeaders = ["Product Name", "Inventory", "Category", "Price"];
  const fieldNames = ["name", "inventoryCount", ["category", "name"], "price"]; //fields of json to be displayed

  const initialPaginationState = {
    pageNr: 1,
    totalNrOfPages: 1,
  };

  const sortOptions = [
    { value: "createdAt_-1", label: "Date: Newest" },
    { value: "createdAt_1", label: "Date: Oldest" },
    { value: "name_1", label: "Name: A-Z" },
    { value: "name_-1", label: "Name: Z-A" },
    { value: "inventoryCount_-1", label: "Inventory: High To Low" },
    { value: "inventoryCount_1", label: "Inventory: Low To High" },
    { value: "price_-1", label: "Price: High To Low" },
    { value: "price_1", label: "Price: Low To High" },
  ];

  const filterOptions = [
    { value: "", label: "None" },
    { value: "inStock_1", label: "In Stock" },
    { value: "inStock_0", label: "Out Of Stock" },
  ];

  const logout = useLogout();
  const { stayLoggedIn } = useSelector((state) => state.user);

  const [products, setProducts] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [paginationState, setPaginationState] = useState(
    {...initialPaginationState}
  );
  const [updateProducts, setUpdateProducts] = useState(true);

  const [sortOption, setSortOption] = useState(sortOptions[0]?.value);
  const [filterOption, setFilterOption] = useState(filterOptions[0]?.value);
const [showAlertMsg, setShowAlertMsg] = useState(false);

  const fetchProducts = useCallback (async (pageNr = null) => {
    // returns json with {products:[json], pageNr:(Number), totalPagesRequired:(Number)}
    setIsLoading(true);
    try {
      const token = getStorageToken(stayLoggedIn);
      const abortController = new AbortController();
      const res = await adminFetchProductsApi(
        token,
        abortController.signal,
        sortOption,
        pageNr,
        filterOption
      );
      return res;
    } catch (error) {
      if (error?.status === 401) {
        //unautorized, eg token expired
        logout();
      }
      setErrorMsg("Could not load data. Please try again later.");
    } finally {
      setIsLoading(false);
      window.scrollTo(0, 0);
    }
  }, [stayLoggedIn, sortOption, filterOption, logout]);

  useEffect(() => {
    if(updateProducts){
      const pageNr = paginationState?.pageNr || 1;
      setUpdateProducts(false);
    fetchProducts(pageNr)
      .then((res) => {
        res.products.forEach((product) => {
          product.category = product?.categories?.[0] || { name: "N/A" };
        });
        setProducts(res.products);
        setPaginationState({
          pageNr: res?.currPageNr || 1,
          totalNrOfPages: res?.totalNrOfPages || 1,
        });
      })
      .catch((error) =>
        setErrorMsg("Could not load data. Please try again later.")
        );
      }
  }, [updateProducts, filterOption, sortOption, paginationState?.pageNr, fetchProducts]);


  const handleChangePageNr = (newPageNr) => {
    if (newPageNr < 1) {
      newPageNr = 1;
    } else if (newPageNr > paginationState.totalNrOfPages) {
      newPageNr = paginationState.totalNrOfPages;
    }
    setPaginationState({ ...paginationState, pageNr: newPageNr });
    setUpdateProducts(true);
  };

  const handleConfirm = async () => {
    // calls api function to delete a a product
      try {
        setIsLoading(true);
        const token = getStorageToken(stayLoggedIn);
        const abortController = new AbortController();
        const { images } = await adminDeleteProductApi(
          token,
          showAlertMsg.payload,
          abortController.signal
        );
        //delete cloudinary images in background
        try {
          const publicIds = images.map((image) => image.public_id);
          deleteCloudinaryImagesApi(token, publicIds);
        } catch (error) {
          console.log("err image deletion");
        }
        // fetch products again
        setUpdateProducts(true);
        setSuccessMsg("The product has been deleted."); //after isLoading finished
      } catch (error) {
        if (error?.status === 401) {
          //unautorized, eg token expired
          logout();
        }
        setErrorMsg(
          "The product could not be deleted. Please try again later. "
        );
      } finally {
        setIsLoading(false);
      }
  };

const handleChangeSortKey = (newKey) => {
  setSortOption(newKey); 
  setUpdateProducts(true);
}

const handleChangeFilterKey = (newKey) => {
  setFilterOption(newKey);
  setUpdateProducts(true);
}

  return (
    <Container className="mt-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      {successMsg && (
        <ModalMessage
          autoClose={true}
          type="success"
          message={successMsg}
          setSuccessMsg={setSuccessMsg}
        />
      )}
      <h1>Product List</h1>
      {isLoading && <LoadingSpinner />}

      {products && (
        <>
          <LinkContainer to="/admin/create-product">
            <Button variant="primary">Add Product</Button>
          </LinkContainer>

          <Row xs={2} md={3} lg={4} className="mt-4 mb-1">
            <Col>
          {/* SORT MENU*/}
              <SortDropdown
                sortOptions={sortOptions}
                handleChangeSortKey={handleChangeSortKey}
              />
            </Col>
            <Col>
            {/* FILTER MENU */}
              <SortDropdown
                sortOptions={filterOptions}
                heading="Filter By"
                handleChangeSortKey={handleChangeFilterKey}
              />
            </Col>
          </Row>
          <ListTable
            dataArray={products}
            headers={tableHeaders}
            fieldNames={fieldNames}
            edit={`/admin/edit-product`}
            delete={(productId) => {setShowAlertMsg({show: true, payload: productId})}}
            view={`/product-details`}
            admin={true}
          />
        </>
      )}

      {/* PAGINATION MENU */}
      {products && products.length > 0 && (
        <PaginationMenu
          pageNr={paginationState.pageNr}
          totalNrOfPages={paginationState.totalNrOfPages}
          handleChangePageNr={handleChangePageNr}
        />
      )}
                  {/* alert message */}
                  {showAlertMsg?.show && 
      <ModalMessage
          message="Are you sure you want to delete this product?" 
          // setErrorMsg={setShowLogoutMsg}
          confirmButton={true}
          handleConfirm={handleConfirm}
          setShowAlertMsg={setShowAlertMsg}
        />
      }
    </Container>
  );
}
